import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { MarketingLayout, Eyebrow, useHydratedTranslation } from "@/components/marketing/Layout";
import { INDUSTRY_BY_SLUG, INDUSTRIES } from "@/lib/industries";
import { useAutoTranslate } from "@/components/AutoTranslate";
import { ArrowRight, Check, MessageSquareQuote, Loader2 } from "lucide-react";

const SITE = "https://realitytwin.io";

export const Route = createFileRoute("/industries/$slug")({
  loader: ({ params }) => {
    if (!INDUSTRY_BY_SLUG[params.slug]) throw notFound();
    return null;
  },
  head: ({ params }) => {
    const i = INDUSTRY_BY_SLUG[params.slug];
    if (!i)
      // Unknown slug: the loader throws notFound, but a crawler that reached
      // this URL still needs a sane title — and must not index it.
      return buildSeo({
        path: `/industries/${params.slug}`,
        title: "AI Twins by industry | Reality Twin",
        description:
          "See how an AI Twin works in your industry: it answers buyer questions from your own documents, qualifies enquiries and books the next step for you.",
        // An unknown slug is a 404 in disguise — never let it into the index.
        noindex: true,
      });
    const url = `${SITE}/industries/${i.slug}`;
    const seo = buildSeo({
      path: `/industries/${i.slug}`,
      title: i.metaTitle,
      description: i.metaDescription,
      type: "article",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: i.metaTitle,
            description: i.metaDescription,
            author: { "@type": "Organization", name: "Reality Twin" },
            publisher: { "@type": "Organization", name: "Reality Twin", url: SITE },
            mainEntityOfPage: url,
            about: { "@type": "Thing", name: i.name },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: i.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: IndustryPage,
});

function IndustryPage() {
  const t = useHydratedTranslation();
  const { slug } = Route.useParams();
  const i = INDUSTRY_BY_SLUG[slug];

  // Page copy is authored in English; translate it into the reader's language
  // through the cached translator, same as the competitor pages.
  const src = i
    ? [
        i.name,
        i.heroEmphasis,
        i.intro,
        i.painTitle,
        ...i.pains,
        ...i.gains.flatMap((g) => [g.title, g.body]),
        ...i.queries,
        ...i.faqs.flatMap((f) => [f.q, f.a]),
      ]
    : [];
  const { out, busy } = useAutoTranslate(src);
  if (!i) return null;
  let n = 0;
  const name = out[n++];
  const heroEmphasis = out[n++];
  const intro = out[n++];
  const painTitle = out[n++];
  const pains = i.pains.map(() => out[n++]);
  const gains = i.gains.map(() => ({ title: out[n++], body: out[n++] }));
  const queries = i.queries.map(() => out[n++]);
  const faqs = i.faqs.map(() => ({ q: out[n++], a: out[n++] }));

  const others = INDUSTRIES.filter((x) => x.slug !== i.slug).slice(0, 5);

  return (
    <MarketingLayout>
      <article className="py-14 sm:py-20">
        {/* HERO */}
        <div className="mx-auto max-w-3xl px-6">
          <Eyebrow>
            <span className="inline-flex items-center gap-2">
              {name}
              {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            </span>
          </Eyebrow>
          <h1 className="mt-4 font-serif text-[36px] sm:text-[56px] leading-[1.03] tracking-[-0.03em]">
            {t("routes.industries.h1", { defaultValue: "Your company, reachable by AI" })}{" "}
            <em className="italic text-muted-foreground">{heroEmphasis}</em>
          </h1>
          <p className="mt-6 text-[17px] leading-[1.6] text-muted-foreground">{intro}</p>
          <div className="mt-7 flex flex-col sm:flex-row flex-wrap gap-2">
            <Link
              to="/twin-check"
              className="inline-flex justify-center items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90"
            >
              {t("routes.industries.ctaCheck", {
                defaultValue: "Check your AI-readability — free",
              })}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link
              to="/auth"
              className="inline-flex justify-center items-center rounded-full hairline bg-card px-5 py-3 text-[14px] font-medium hover:bg-cream transition"
            >
              {t("routes.industries.ctaStart", { defaultValue: "Build your Twin" })}
            </Link>
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14 space-y-12">
          {/* PAIN */}
          <section>
            <h2 className="font-serif text-[28px] sm:text-[32px] leading-[1.1] tracking-[-0.02em]">
              {painTitle}
            </h2>
            <ul className="mt-5 space-y-3">
              {pains.map((p, k) => (
                <li
                  key={k}
                  className="flex gap-3 text-[15.5px] leading-relaxed text-muted-foreground"
                >
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-foreground/40 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* GAINS */}
          <section>
            <h2 className="font-serif text-[28px] sm:text-[32px] leading-[1.1] tracking-[-0.02em]">
              {t("routes.industries.gainsTitle", { defaultValue: "What a Twin changes" })}
            </h2>
            <div className="mt-6 grid gap-4">
              {gains.map((g, k) => (
                <div key={k} className="hairline rounded-2xl bg-card p-6">
                  <div className="flex items-start gap-2.5">
                    <Check className="h-4 w-4 mt-1 shrink-0" strokeWidth={2.5} />
                    <div>
                      <div className="font-medium text-[16px]">{g.title}</div>
                      <p className="mt-1.5 text-[14.5px] leading-relaxed text-muted-foreground">
                        {g.body}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* THE QUERIES THEIR BUYERS ACTUALLY TYPE */}
          <section className="hairline rounded-2xl bg-cream p-6 sm:p-8">
            <div className="flex items-center gap-1.5 font-mono-eyebrow">
              <MessageSquareQuote className="h-3.5 w-3.5" />
              {t("routes.industries.queriesTitle", {
                defaultValue: "What your buyers ask an AI assistant",
              })}
            </div>
            <ul className="mt-4 space-y-2.5">
              {queries.map((q, k) => (
                <li key={k} className="text-[15px] leading-relaxed">
                  <span className="text-muted-foreground">“</span>
                  {q}
                  <span className="text-muted-foreground">”</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[13.5px] text-muted-foreground">
              {t("routes.industries.queriesNote", {
                defaultValue:
                  "If your company can't be read by a model, the answer names someone else.",
              })}
            </p>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="font-serif text-[28px] sm:text-[32px] leading-[1.1] tracking-[-0.02em]">
              {t("routes.industries.faqTitle", { defaultValue: "Questions we get asked" })}
            </h2>
            <div className="mt-6 space-y-4">
              {faqs.map((f, k) => (
                <div key={k} className="hairline rounded-2xl bg-card p-5 sm:p-6">
                  <h3 className="font-serif text-[19px] leading-tight">{f.q}</h3>
                  <p className="mt-2 text-[14.5px] leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA + internal links */}
          <section className="hairline rounded-2xl bg-foreground text-background p-6 sm:p-9">
            <h2 className="font-serif text-[26px] sm:text-[32px] leading-[1.1] tracking-[-0.02em]">
              {t("routes.industries.ctaTitle", {
                defaultValue: "See how AI reads your company today",
              })}
            </h2>
            <p className="mt-3 text-[15px] opacity-80">
              {t("routes.industries.ctaBody", {
                defaultValue:
                  "Free, no account. Paste your website and get the findings — then ask your own Twin a question.",
              })}
            </p>
            <Link
              to="/twin-check"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-background text-foreground px-5 py-3 text-[14px] font-medium hover:opacity-90"
            >
              {t("routes.industries.ctaCheck", {
                defaultValue: "Check your AI-readability — free",
              })}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </section>

          <section>
            <div className="font-mono-eyebrow">
              {t("routes.industries.more", { defaultValue: "Other industries" })}
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to="/industries/$slug"
                  params={{ slug: o.slug }}
                  className="hairline rounded-xl bg-card p-4 hover:bg-cream transition"
                >
                  <div className="text-[15px] font-medium">{o.name}</div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </article>
    </MarketingLayout>
  );
}
